import { useState, useCallback, useEffect, useRef } from 'react';
import { 
  documentService, 
  UploadResponse, 
  ProcessingStatusResponse, 
  ProcessingResult 
} from '../services/document';
import { useAuth } from '../../../contexts/AuthContext';
import { apiService } from '../../../services/api';
import { indexedDBService, StoredDocument } from '../services/indexedDB';

export interface DocumentProcessingState {
  // Upload state
  isUploading: boolean;
  uploadError: string | null;
  uploadedDocument: UploadResponse | null;
  
  // Processing state
  isProcessing: boolean;
  processingStatus: ProcessingStatusResponse | null;
  processingError: string | null;
  
  // Results
  processingResult: ProcessingResult | null;
  
  // PDF viewing
  pdfBlob: Blob | null;
  originalFile: File | null;
  
  // UI state
  currentStep: 'upload' | 'processing' | 'completed' | 'error';
}

export const useDocumentProcessing = () => {
  const { getAccessToken } = useAuth();
  const [state, setState] = useState<DocumentProcessingState>({
    isUploading: false,
    uploadError: null,
    uploadedDocument: null,
    isProcessing: false,
    processingStatus: null,
    processingError: null,
    processingResult: null,
    pdfBlob: null,
    originalFile: null,
    currentStep: 'upload'
  });

  const statusPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  
  // Update the ref whenever getAccessToken changes
  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  // Poll processing status
  const pollStatus = useCallback((documentId: string) => {
    // Clear any existing interval
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
    }

    const interval = setInterval(async () => {
      try {
        // Get fresh token before polling
        const token = await getAccessTokenRef.current();
        if (token) {
          apiService.setAuthToken(token);
        }
        
        const statusResponse = await documentService.getProcessingStatus(documentId);
        
        setState(prev => ({
          ...prev,
          processingStatus: statusResponse
        }));

        // Check if processing is complete
        if (statusResponse.status === 'completed') {
          clearInterval(interval);
          statusPollingIntervalRef.current = null;
          
          // Fetch the final result
          try {
            // Get fresh token before fetching result
            const token = await getAccessTokenRef.current();
            if (token) {
              apiService.setAuthToken(token);
            }
            const result = await documentService.getProcessingResult(documentId);
            
            // Store the original markdown in IndexedDB
            try {
              await indexedDBService.updateOriginalMarkdown(documentId, result.redacted_content);
            } catch (dbError) {
              console.error('Failed to store original markdown:', dbError);
            }
            
            setState(prev => ({
              ...prev,
              isProcessing: false,
              processingResult: result,
              currentStep: 'completed'
            }));
          } catch (resultError: any) {
            console.error('Failed to fetch result:', resultError);
            setState(prev => ({
              ...prev,
              isProcessing: false,
              processingError: 'Failed to fetch processing result',
              currentStep: 'error'
            }));
          }
        } else if (statusResponse.status === 'failed') {
          clearInterval(interval);
          statusPollingIntervalRef.current = null;
          setState(prev => ({
            ...prev,
            isProcessing: false,
            processingError: statusResponse.error_message || 'Processing failed',
            currentStep: 'error'
          }));
        }

      } catch (error: any) {
        console.error('Status polling error:', error);
        // Don't stop polling on network errors, just log them
      }
    }, 2000); // Poll every 2 seconds

    statusPollingIntervalRef.current = interval;
  }, []);

  // Start processing
  const startProcessing = useCallback(async (documentId: string) => {
    try {
      setState(prev => ({
        ...prev,
        isProcessing: true,
        processingError: null,
        currentStep: 'processing'
      }));

      // Get fresh token and set it before processing
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }

      await documentService.startProcessing(documentId);
      
      // Start polling for status updates
      pollStatus(documentId);

    } catch (error: any) {
      console.error('Processing start error:', error);
      setState(prev => ({
        ...prev,
        isProcessing: false,
        processingError: error.response?.data?.detail || error.message || 'Failed to start processing',
        currentStep: 'error'
      }));
    }
  }, [pollStatus, getAccessToken]);

  // Upload file
  const uploadFile = useCallback(async (file: File) => {
    try {
      // Validate file first
      const validation = documentService.validateFile(file);
      if (!validation.valid) {
        setState(prev => ({
          ...prev,
          uploadError: validation.error || 'Invalid file',
          currentStep: 'error'
        }));
        return;
      }

      // Check if it's a PDF - if so, immediately store and display it
      const isPDF = file.name.toLowerCase().endsWith('.pdf');
      if (isPDF) {
        setState(prev => ({
          ...prev,
          pdfBlob: file,
          originalFile: file
        }));
      }

      setState(prev => ({
        ...prev,
        isUploading: true,
        uploadError: null,
        originalFile: file,
        currentStep: 'upload'
      }));

      // Get fresh token and set it before uploading
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      } else {
        throw new Error('Failed to get authentication token');
      }

      const uploadResponse = await documentService.uploadDocument(file);
      
      // Handle converted PDF blob if Word document was converted
      let pdfBlobToStore: Blob | null = null;
      
      if (uploadResponse.needs_conversion && uploadResponse.pdf_blob) {
        // Convert base64 to Blob
        const pdfBytes = atob(uploadResponse.pdf_blob);
        const pdfArray = new Uint8Array(pdfBytes.length);
        for (let i = 0; i < pdfBytes.length; i++) {
          pdfArray[i] = pdfBytes.charCodeAt(i);
        }
        pdfBlobToStore = new Blob([pdfArray], { type: 'application/pdf' });
        
        setState(prev => ({
          ...prev,
          pdfBlob: pdfBlobToStore
        }));
      } else if (isPDF) {
        // For PDFs, we already set the blob above
        pdfBlobToStore = file;
      }
      
      // Store document in IndexedDB immediately after upload
      try {
        await indexedDBService.init();
        
        const storedDoc: StoredDocument = {
          documentId: uploadResponse.document_id,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          uploadedAt: new Date(),
          originalFile: file, // Keep original for reference
          lastModified: new Date(),
          metadata: {
            processingStatus: 'uploaded',
            needs_conversion: uploadResponse.needs_conversion
          }
        };
        
        // Store the PDF blob for converted DOCX files
        if (pdfBlobToStore) {
          (storedDoc as any).pdfBlob = pdfBlobToStore;
        }
        
        await indexedDBService.saveDocument(storedDoc);
      } catch (dbError) {
        console.error('Failed to store document in IndexedDB:', dbError);
        // Don't fail the upload if IndexedDB storage fails
      }
      
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadedDocument: uploadResponse,
        currentStep: 'upload'
      }));

      // Automatically start processing after upload
      startProcessing(uploadResponse.document_id);

    } catch (error: any) {
      console.error('Upload error:', error);
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadError: error.response?.data?.detail || error.message || 'Upload failed',
        currentStep: 'error'
      }));
    }
  }, [startProcessing, getAccessToken]);

  // Retry processing
  const retryProcessing = useCallback(async (documentId: string) => {
    try {
      setState(prev => ({
        ...prev,
        isProcessing: true,
        processingError: null,
        processingResult: null,
        currentStep: 'processing'
      }));

      // Get fresh token and set it before retrying
      const token = await getAccessToken();
      if (token) {
        apiService.setAuthToken(token);
      }

      await documentService.retryProcessing(documentId);
      pollStatus(documentId);

    } catch (error: any) {
      console.error('Retry processing error:', error);
      setState(prev => ({
        ...prev,
        isProcessing: false,
        processingError: error.response?.data?.detail || error.message || 'Failed to retry processing',
        currentStep: 'error'
      }));
    }
  }, [pollStatus, getAccessToken]);

  // Reset state for new upload
  const resetState = useCallback(() => {
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }
    
    setState({
      isUploading: false,
      uploadError: null,
      uploadedDocument: null,
      isProcessing: false,
      processingStatus: null,
      processingError: null,
      processingResult: null,
      pdfBlob: null,
      originalFile: null,
      currentStep: 'upload'
    });
  }, []);

  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (statusPollingIntervalRef.current) {
        clearInterval(statusPollingIntervalRef.current);
      }
    };
  }, []);

  return {
    ...state,
    uploadFile,
    startProcessing,
    retryProcessing,
    resetState
  };
};